import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import { BasicAuthFields } from '../components/BasicAuthForm.js';
import { Button, Card, Checkbox, Notice, TextField } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { PENDING_SITE_KEY } from './Auth.js';

/** US-02: enter an address, we normalise and add it, then follow its first checks. */
export function AddSite() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useQueryClient();
  // Prefilled after registering from a Quick-Check (Register stores the address).
  const [url, setUrl] = useState(() => {
    try {
      return localStorage.getItem(PENDING_SITE_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const [name, setName] = useState('');
  const [protectedSite, setProtectedSite] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const add = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/sites', {
          body: {
            url: url.trim(),
            ...(name.trim() ? { name: name.trim() } : {}),
            ...(protectedSite && username.trim() && password
              ? { basicAuth: { username: username.trim(), password } }
              : {}),
          },
        }),
      ),
    onSuccess: async (site) => {
      try {
        localStorage.removeItem(PENDING_SITE_KEY);
      } catch {
        // nothing stored
      }
      await client.invalidateQueries({ queryKey: ['sites'] });
      void navigate(`/sites/${site.id}/onboarding`, {
        state: { unreachable: !site.reachability.reachable },
      });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return setError(t('errors.required'));
    setError(undefined);
    add.mutate();
  };

  let failure: string | undefined;
  if (add.error instanceof ApiError) {
    if (add.error.code === 'PLAN_LIMIT_REACHED')
      failure = t('addSite.limit', { max: add.error.problem.limit?.max ?? 1 });
    else if (add.error.code === 'CONFLICT') failure = t('addSite.exists');
    else failure = errorMessage(add.error, t);
  } else if (add.error) failure = errorMessage(add.error, t);

  return (
    <>
      <PageTitle>{t('addSite.title')}</PageTitle>
      <Card className="max-w-xl">
        <p className="mb-4 text-slate-800">{t('addSite.intro')}</p>
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          {failure ? <Notice kind="error">{failure}</Notice> : null}
          <TextField
            label={t('addSite.url')}
            hint={t('addSite.urlHint')}
            inputMode="url"
            autoComplete="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            error={error}
            required
          />
          <TextField
            label={t('addSite.name')}
            hint={t('addSite.nameHint')}
            optional
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Checkbox
            label={t('basicAuth.addToggle')}
            checked={protectedSite}
            onChange={(e) => setProtectedSite(e.target.checked)}
          />
          {protectedSite ? (
            <div className="flex flex-col gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
              <p className="text-sm text-slate-800">{t('basicAuth.addText')}</p>
              <BasicAuthFields
                username={username}
                password={password}
                onUsername={setUsername}
                onPassword={setPassword}
              />
            </div>
          ) : null}
          <Button type="submit" busy={add.isPending}>
            {add.isPending ? t('addSite.checking') : t('addSite.submit')}
          </Button>
        </form>
      </Card>
    </>
  );
}
